import { __ } from "@wordpress/i18n";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import React from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	Background,
	ColorPicker,
	Dimensions,
	IconPicker,
	PopoverDrawer,
	Slider,
	UrlInput,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			link,
			icon,
			iconSize,
			iconColor,
			iconHoverColor,
			iconBgColor,
			iconBgHoverColor,
			blockPadding,
			blockMargin,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
		},
		setAttributes,
	} = props;
	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("General", "magazine-blocks")} initialOpen>
					<UrlInput
						label={__("URL", "magazine-blocks")}
						onChange={(val) => setAttributes({ link: val })}
						value={link}
						placeholder="https://"
						newTab
					/>
				</Panel>
				<Panel title={__("Icons", "magazine-blocks")}>
					<IconPicker
						hasToggle={true}
						value={icon || { enable: true, icon: "facebook" }}
						onChange={(val) => setAttributes({ icon: val })}
					/>
					<Slider
						onChange={(val: any) =>
							setAttributes({ iconSize: val })
						}
						label={__("Size", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={0}
						max={40}
						value={iconSize}
					/>
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("Icon", "blockart")} initialOpen>
					<PopoverDrawer
						label={__("Color", "blockart")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: iconColor,
											id: "iconColor",
										},
										{
											color: iconHoverColor,
											id: "iconHoverColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "blockart")}</Tab>
								<Tab>{__("Hover", "blockart")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={iconColor}
										resetKey="iconColor"
										onReset={(v) => {
											setAttributes({ iconColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={iconColor}
										onChange={(v) =>
											setAttributes({ iconColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={iconHoverColor}
										resetKey="iconHoverColor"
										onReset={(v) => {
											setAttributes({
												iconHoverColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={iconHoverColor}
										onChange={(v) =>
											setAttributes({ iconHoverColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Background", "blockart")}>
					<PopoverDrawer label={__("Type", "blockart")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "blockart")}</Tab>
								<Tab>{__("Hover", "blockart")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={iconBgColor}
										resetKey="iconBgColor"
										onReset={(v) => {
											setAttributes({ iconBgColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={iconBgColor}
										onChange={(v) =>
											setAttributes({ iconBgColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={iconBgHoverColor}
										resetKey="iconBgHoverColor"
										onReset={(v) => {
											setAttributes({
												iconBgHoverColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={iconBgHoverColor}
										onChange={(v) =>
											setAttributes({
												iconBgHoverColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
